import { OptionallyVisible } from 'components';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { PayoutDestinationDetails, PayoutDestinationType } from 'services/payouts/types';

import { Button } from 'components/buttons';
import { CurrencySelect } from 'components/inputs/select/currencySelect/currencySelect';
import { CurrencyItemWithNetwork } from 'components/inputs/select/currencySelect/types';
import { Buttons } from 'components/modal/styles';

import { PWCAsset } from 'types/assets';
import { CurrencyCode } from 'types/currency';

import { getPWCCryptoLabel } from 'utils/currencies';

import { usePayout } from '../context';
import { usePayoutSetupStep } from '../context/modal';
import { SecondaryText } from '../fiat/styles';
import { Delimiter, Label, Section } from '../styles';
import { PayoutType } from '../types';
import LABELS from './keys';

interface CryptoPayoutProps {
  assets?: PWCAsset[];
  payoutDestinations?: PayoutDestinationDetails[];
}

const CryptoPayout = ({ assets = [], payoutDestinations = [] }: CryptoPayoutProps) => {
  const { t, i18n } = useTranslation();
  const payoutStep = usePayoutSetupStep();
  const payout = usePayout();
  // TODO: We map everything 1/1 for now so it has to stay USD.
  const feesCurrencyCode = CurrencyCode.USD;

  const mapAssetToCryptoItem = (asset: PWCAsset): CurrencyItemWithNetwork => ({
    key: asset.cryptoCurrencyCode,
    icon: asset.iconUrl,
    name: asset.name,
    label: getPWCCryptoLabel(asset),
    code: asset.cryptoCurrencyCode,
    network: asset.network,
  });

  const initialCryptoItem = payout[PayoutType.Crypto]?.cryptocurrency
    ? mapAssetToCryptoItem(payout[PayoutType.Crypto].cryptocurrency)
    : undefined;
  const [selectedAsset, setSelectedAsset] = useState<CurrencyItemWithNetwork | undefined>(initialCryptoItem);

  const getFormattedCryptoPayoutFee = (asset: CurrencyItemWithNetwork | undefined): string | null => {
    if (!asset) {
      return null;
    }

    const matchingDestination = payoutDestinations.find(
      (destination) =>
        destination.type === PayoutDestinationType.CRYPTO &&
        destination.assets.some((a) => a === asset.key) &&
        destination.network === asset.network,
    );

    const optionFee = matchingDestination?.fee ?? null;
    if (optionFee === null) {
      return null;
    }

    const formattedFee = new Intl.NumberFormat(i18n.language, {
      style: 'currency',
      currency: feesCurrencyCode,
    }).format(optionFee.fixed);
    const formattedPercentage = optionFee.percent ? `${optionFee.percent}% + ` : '';

    return `${formattedPercentage}${formattedFee}`;
  };

  const formattedFee = getFormattedCryptoPayoutFee(selectedAsset);
  const disclaimerText = formattedFee ? `${t(LABELS.CRYPTO_PAYOUT_FEES)} ${formattedFee}` : null;

  const handleAssetChange = (item: CurrencyItemWithNetwork | CurrencyItemWithNetwork[]) => {
    if (Array.isArray(item)) {
      setSelectedAsset(item[0]);
      return;
    }
    setSelectedAsset(item);
  };

  const cryptoItems = assets.map(mapAssetToCryptoItem);

  const onContinue = () => {
    const asset = assets.find((asset) => asset.cryptoCurrencyCode === selectedAsset?.code && asset.network === selectedAsset?.network);
    if (!asset) {
      throw new Error('Selected asset not found in the assets list');
    }

    payout.setPayoutState({
      ...payout,
      [PayoutType.Crypto]: {
        cryptocurrency: asset,
      },
    });
    payoutStep.proceed();
  };

  return (
    <>
      <Section>
        <Label>{t(LABELS.CRYPTOCURRENCY_SELECT_LABEL)}</Label>
        <CurrencySelect
          fullWidth
          value={selectedAsset as CurrencyItemWithNetwork | undefined}
          items={cryptoItems}
          getItemLabel={(item) => item.name}
          onChange={handleAssetChange}
          placeholder={t(LABELS.CRYPTOCURRENCY_SELECT_PLACEHOLDER)}
          closeDropdownOnSelect
        />
        <OptionallyVisible visible={Boolean(disclaimerText)}>
          <SecondaryText>{disclaimerText}</SecondaryText>
        </OptionallyVisible>
      </Section>
      <Delimiter />
      <Buttons>
        <Button secondary onClick={payoutStep.close}>
          {t(LABELS.ACTIONS.CANCEL)}
        </Button>
        <Button primary onClick={onContinue} disabled={!selectedAsset}>
          {t(LABELS.ACTIONS.CONTINUE)}
        </Button>
      </Buttons>
    </>
  );
};

export default CryptoPayout;
